import { useState, useEffect } from 'react';
import { BorrowerDropDown } from './components/BorrowerDropDown';
import { BorrowerInformation } from './components/BorrowerInformation/BorrowerInformation';
import { BusinessMailingAddress } from './components/BusinessMailingAddress/BusinessMailingAddress';
import { LoanType } from '@apply/constants';
import { useBorrowerIsFirstLoan, useBorrowerShouldAskLegalQuestions, useBorrowerUsingPmc, useBorrowerMasterNoteId } from '../../../../queries/BorrowerFields/CoreFields';
import { useBorrowerEntity } from '../../../../queries/BorrowerFields/EntityFields';
import { FirstLoanReachout } from './components/FirstLoanReachout/FirstLoanReachout';
import { LegalityVerification } from './components/LegalityVerification/LegalityVerification';
import { ShowIf } from '../../../components/ShowIf';
import { useLocation } from 'react-router';
import { PrincipalContactInfo } from './components/PrincipalContactInfo';
import { EntityListResult } from './interfaces/EntityListResult';
import { ApplyApi } from '@apply/api';
import { applicationInsights } from '../../../../ApplicationInsights';
import { PropertyManagement } from '../PropertyManagement';
import { ManagementAgreementProvideLaterAdvisory } from './components/ManagementAgreementProvideLaterAdvisory';
import { PropertyManagementCompany } from './components/PropertyManagementCompany/PropertyManagementCompany';

export interface BorrowerDataProps {
    loanType: LoanType | undefined;
    isReview?: boolean;
    set_disableWizardButtons?: React.Dispatch<React.SetStateAction<boolean>>;
    entryGuid?: string;
}

export const BorrowerData: React.FC<BorrowerDataProps> = ({
    loanType = undefined,
    isReview = false,
    set_disableWizardButtons,
    entryGuid,
}) => {
    const applyApi = new ApplyApi.ApplyApi(applicationInsights);
    const { pathname } = useLocation();

    const [isFirstLoan, set_isFirstLoan] = useBorrowerIsFirstLoan();
    const [shouldAskLegalQuestions, set_shouldAskLegalQuestions] = useBorrowerShouldAskLegalQuestions();
    const [willUsePropertyManagement] = useBorrowerUsingPmc();
    const [currentEntity, set_entity] = useBorrowerEntity();
    const [, set_masterNoteId] = useBorrowerMasterNoteId();
    const [entityList, set_entityList] = useState<EntityListResult>();
    const [borrowerSelected, setBorrowerSelected] = useState<boolean>(!!entryGuid);
    const [borrowerFormKey, setBorrowerFormKey] = useState(0);

    useEffect(() => {
        const _loadBorrowerList = async () => {
            const getEntities = await applyApi.getEntityList();
            if (getEntities.statusCode !== 200) throw new Error('Entity Retrieval failed');

            const entityList = getEntities;
            set_entityList(entityList);
        };
        _loadBorrowerList();
    }, [loanType, pathname]);

    // Load borrower info flags on mount (to show questions immediately, not just after first save)
    useEffect(() => {
        const _loadBorrowerInfo = async () => {
            const borrowerInfo = await applyApi.getBorrowerInfo();
            if (borrowerInfo.statusCode === 200 && borrowerInfo.result) {
                set_isFirstLoan(borrowerInfo.result.isFirstLoan);
                set_shouldAskLegalQuestions(borrowerInfo.result.shouldAskLegalQuestions);
            }
        };
        _loadBorrowerInfo();
    }, []);

    // "New Borrower" will already be auto-selected for first-time applicants (no existing entities),
    // so make sure borrowerSelected is true in that case
    useEffect(() => {
        if (!entityList) return;
        const isFirstTimeApp = !entityList.result || !(entityList.result.length as number);
        if (isFirstTimeApp && !borrowerSelected) {
            setBorrowerSelected(true);
        }
    }, [entityList]);

    const onSelectedEntityChanged = async (selectedEntityId: number) => {
        setBorrowerSelected(true);

        if (selectedEntityId == 0) {
            set_entity({
                ...currentEntity!,
                id: 0,
                name: '',
                stateIncorporated: '',
                typeId: undefined as unknown as number,
                address: {
                    street1: '',
                    street2: null,
                    city: '',
                    state: '',
                    zipCode: '',
                    county: null,
                },
            });
            setBorrowerFormKey((prev) => prev + 1);
        } else {
            const entity = entityList?.result.find((b) => b.entityId == selectedEntityId);
            if (!entity) return;

            const isLoc = loanType ? [LoanType.SubNoteConstruction, LoanType.SubNoteFixFlip].includes(loanType) : false;

            set_entity({
                ...currentEntity!,
                id: entity.entityId,
                name: entity.entityName,
                stateIncorporated: entity.entityStateIncorporated,
                typeId: entity.entityType ?? undefined,
                address: {
                    street1: entity.address.street1,
                    street2: entity.address.street2,
                    city: entity.address.city,
                    state: entity.address.state,
                    zipCode: entity.address.zipCode,
                    county: currentEntity?.address.county ?? null,
                },
            });

            if (isLoc) {
                set_masterNoteId(entity.masterNoteId);
            }
        }
    };

    return (
        <>
            <BorrowerDropDown
                loanType={loanType}
                entityList={entityList}
                onChange={onSelectedEntityChanged}
                isReview={isReview}
                borrowerSelected={borrowerSelected}
            />
            <ShowIf showIf={borrowerSelected || isReview}>
                <div key={borrowerFormKey}>
                    <ShowIf showIf={isFirstLoan}>
                        <FirstLoanReachout
                            required={true}
                            isReview={isReview}
                        />
                    </ShowIf>
                    <ShowIf showIf={shouldAskLegalQuestions}>
                        <LegalityVerification
                            required={true}
                            isReview={isReview}
                        />
                    </ShowIf>
                    <ShowIf showIf={isReview}>
                        <PrincipalContactInfo isReview={isReview} />
                    </ShowIf>
                    <BorrowerInformation
                        isReview={isReview}
                        entityList={entityList}
                        set_disableWizardButtons={set_disableWizardButtons}
                    />
                    <BusinessMailingAddress isReview={isReview} />
                </div>
                <ShowIf showIf={loanType === LoanType.Rental || loanType === LoanType.BuildRent}>
                    <PropertyManagement
                        required={true}
                        isReview={isReview}
                    />
                    <ShowIf showIf={willUsePropertyManagement}>
                        <PropertyManagementCompany
                            required={true}
                            isReview={isReview}
                        />
                        <ManagementAgreementProvideLaterAdvisory isReview={isReview} />
                    </ShowIf>
                </ShowIf>
            </ShowIf>
        </>
    );
};
